import React from 'react';
import { AppLoadingState } from './AppLoadingState';
import type { AppNotification, AppNotificationAttachment } from './AppNotificationPopups';

type Props = {
  styles: any;
  messageWorkspaceMode: 'messages' | 'notifications';
  setMessageWorkspaceMode: React.Dispatch<React.SetStateAction<'messages' | 'notifications'>>;
  messagesLoading: boolean;
  messagesQuerySettled: boolean;
  showTechComposePanel: boolean;
  setShowTechComposePanel: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTechMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  messageChannelType: 'external' | 'internal';
  setMessageChannelType: React.Dispatch<React.SetStateAction<'external' | 'internal'>>;
  messageUnreadCounts: { external: number; internal: number };
  messageView: 'active' | 'archived' | 'all';
  setMessageView: React.Dispatch<React.SetStateAction<'active' | 'archived' | 'all'>>;
  messageFocusFilter: 'all' | 'general' | 'device' | 'case';
  setMessageFocusFilter: React.Dispatch<React.SetStateAction<'all' | 'general' | 'device' | 'case'>>;
  messageCaseFilter: string;
  setMessageCaseFilter: React.Dispatch<React.SetStateAction<string>>;
  messageDeviceFilter: string;
  setMessageDeviceFilter: React.Dispatch<React.SetStateAction<string>>;
  messageCaseOptions: any[];
  getCaseDisplayNumber: (entry: any) => string;
  messageDeviceOptions: any[];
  getDeviceDisplayLabel: (device: any) => string;
  messageSortOrder: 'newest' | 'oldest';
  setMessageSortOrder: React.Dispatch<React.SetStateAction<'newest' | 'oldest'>>;
  showUnreadOnly: boolean;
  setShowUnreadOnly: React.Dispatch<React.SetStateAction<boolean>>;
  messageShowSent: boolean;
  setMessageShowSent: React.Dispatch<React.SetStateAction<boolean>>;
  techMessages: any[];
  filteredTechMessages: any[];
  messageVisibleCount: number;
  setMessageVisibleCount: React.Dispatch<React.SetStateAction<number>>;
  readTechMessageIds: string[];
  setReadTechMessageIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedTechMessageId: string | null;
  getTechMessageFocusLabel: (message: any) => string;
  getMessagePreview: (value: string) => string;
  formatDateTime: (value?: string | Date | null) => string | null;
  user: any;
  markTechMessageRead: (messageId: string) => void;
  selectedTechMessage: any | null;
  composeInternalRecipientOptions: any[];
  messageComposeRecipientUserIds: string[];
  setMessageComposeRecipientUserIds: React.Dispatch<React.SetStateAction<string[]>>;
  composeExternalRecipientOptions: any[];
  messageComposeExternalRecipientUserIds: string[];
  setMessageComposeExternalRecipientUserIds: React.Dispatch<React.SetStateAction<string[]>>;
  messageComposeCustomerContactsLoading: boolean;
  messageComposeCustomerId: string;
  setMessageComposeCustomerId: React.Dispatch<React.SetStateAction<string>>;
  composeCustomerOptions: any[];
  messageComposeFocus: 'general' | 'device' | 'case';
  setMessageComposeFocus: React.Dispatch<React.SetStateAction<'general' | 'device' | 'case'>>;
  messageComposeCaseId: string;
  setMessageComposeCaseId: React.Dispatch<React.SetStateAction<string>>;
  cases: any[];
  composeCaseOptions: any[];
  devices: any[];
  composeDeviceOptions: any[];
  messageComposeDeviceId: string;
  setMessageComposeDeviceId: React.Dispatch<React.SetStateAction<string>>;
  messageComposeBody: string;
  setMessageComposeBody: React.Dispatch<React.SetStateAction<string>>;
  handleTechMessageAttachmentChange: (files: FileList | null) => void;
  messageComposeAttachments: any[];
  formatAttachmentSize: (size: number) => string;
  handleRemoveTechMessageAttachment: (attachmentId: string) => void;
  messageActionBusy: 'compose' | 'reply' | null;
  handleSendOutboundMessage: () => void | Promise<void>;
  messageReplyBody: string;
  setMessageReplyBody: React.Dispatch<React.SetStateAction<string>>;
  handleTechReplyAttachmentChange: (files: FileList | null) => void;
  messageReplyAttachments: any[];
  handleRemoveTechReplyAttachment: (attachmentId: string) => void;
  handleArchiveSelectedTechMessage: (archived: boolean) => void | Promise<void>;
  handleRestoreSelectedTechMessage: () => void | Promise<void>;
  handleDeleteSelectedTechMessage: () => void | Promise<void>;
  handleDownloadTechMessageAttachment: (messageId: string, attachment: any) => void | Promise<void>;
  handleOpenTechNotificationAttachment: (
    notification: AppNotification,
    attachment: AppNotificationAttachment
  ) => void | Promise<void>;
  handleSendTechReply: () => void | Promise<void>;
  notifications: AppNotification[];
  notificationsLoading: boolean;
  notificationView: 'active' | 'all' | 'dismissed';
  setNotificationView: React.Dispatch<React.SetStateAction<'active' | 'all' | 'dismissed'>>;
  selectedNotificationId: string | null;
  setSelectedNotificationId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedNotification: AppNotification | null;
  markNotificationRead: (notificationId: string) => void | Promise<void>;
  dismissNotification: (notification: AppNotification) => void | Promise<void>;
  canCreateNotifications: boolean;
  onCreateNotification: () => void;
};

export const TechDashboardMessagesTab = React.memo(function TechDashboardMessagesTab({
  styles,
  messageWorkspaceMode,
  setMessageWorkspaceMode,
  messagesLoading,
  messagesQuerySettled,
  showTechComposePanel,
  setShowTechComposePanel,
  setSelectedTechMessageId,
  messageChannelType,
  setMessageChannelType,
  messageUnreadCounts,
  messageView,
  setMessageView,
  messageFocusFilter,
  setMessageFocusFilter,
  messageCaseFilter,
  setMessageCaseFilter,
  messageDeviceFilter,
  setMessageDeviceFilter,
  messageCaseOptions,
  getCaseDisplayNumber,
  messageDeviceOptions,
  getDeviceDisplayLabel,
  messageSortOrder,
  setMessageSortOrder,
  showUnreadOnly,
  setShowUnreadOnly,
  messageShowSent,
  setMessageShowSent,
  techMessages,
  filteredTechMessages,
  messageVisibleCount,
  setMessageVisibleCount,
  readTechMessageIds,
  setReadTechMessageIds,
  selectedTechMessageId,
  getTechMessageFocusLabel,
  getMessagePreview,
  formatDateTime,
  user,
  markTechMessageRead,
  selectedTechMessage,
  composeInternalRecipientOptions,
  messageComposeRecipientUserIds,
  setMessageComposeRecipientUserIds,
  composeExternalRecipientOptions,
  messageComposeExternalRecipientUserIds,
  setMessageComposeExternalRecipientUserIds,
  messageComposeCustomerContactsLoading,
  messageComposeCustomerId,
  setMessageComposeCustomerId,
  composeCustomerOptions,
  messageComposeFocus,
  setMessageComposeFocus,
  messageComposeCaseId,
  setMessageComposeCaseId,
  cases,
  composeCaseOptions,
  devices,
  composeDeviceOptions,
  messageComposeDeviceId,
  setMessageComposeDeviceId,
  messageComposeBody,
  setMessageComposeBody,
  handleTechMessageAttachmentChange,
  messageComposeAttachments,
  formatAttachmentSize,
  handleRemoveTechMessageAttachment,
  messageActionBusy,
  handleSendOutboundMessage,
  messageReplyBody,
  setMessageReplyBody,
  handleTechReplyAttachmentChange,
  messageReplyAttachments,
  handleRemoveTechReplyAttachment,
  handleArchiveSelectedTechMessage,
  handleRestoreSelectedTechMessage,
  handleDeleteSelectedTechMessage,
  handleDownloadTechMessageAttachment,
  handleOpenTechNotificationAttachment,
  handleSendTechReply,
  notifications,
  notificationsLoading,
  notificationView,
  setNotificationView,
  selectedNotificationId,
  setSelectedNotificationId,
  selectedNotification,
  markNotificationRead,
  dismissNotification,
  canCreateNotifications,
  onCreateNotification
}: Props) {
  const composeAttachmentInputRef = React.useRef<HTMLInputElement | null>(null);
  const replyAttachmentInputRef = React.useRef<HTMLInputElement | null>(null);
  const [recipientSearchTerm, setRecipientSearchTerm] = React.useState('');
  const [recipientPickerOpen, setRecipientPickerOpen] = React.useState(false);
  const [externalRecipientSeedKey, setExternalRecipientSeedKey] = React.useState('');
  const [composeCustomerSearchTerm, setComposeCustomerSearchTerm] = React.useState('');
  const [composeCaseSearchTerm, setComposeCaseSearchTerm] = React.useState('');
  const [composeCaseCustomerFilter, setComposeCaseCustomerFilter] = React.useState('');
  const [composeCaseDeviceFilter, setComposeCaseDeviceFilter] = React.useState('');
  const [composeDeviceSearchTerm, setComposeDeviceSearchTerm] = React.useState('');
  const [composeDeviceCustomerFilter, setComposeDeviceCustomerFilter] = React.useState('');
  const [composeDeviceModelFilter, setComposeDeviceModelFilter] = React.useState('');
  const [composeDeviceLocationFilter, setComposeDeviceLocationFilter] = React.useState('');
  const showInboxQueryLoading = !messagesQuerySettled;
  const unreadNotificationCount = notifications.filter(notification => !notification.readAt && !notification.dismissedAt).length;
  const notificationTabBaseStyle = {
    borderColor: '#10b981',
    backgroundColor: '#ecfdf5',
    color: '#047857'
  };
  const notificationTabActiveStyle = {
    borderColor: '#047857',
    backgroundColor: '#047857',
    color: '#ffffff'
  };
  const notificationCountStyle =
    messageWorkspaceMode === 'notifications'
      ? {
          backgroundColor: 'rgba(255, 255, 255, 0.24)',
          color: '#ffffff'
        }
      : {
          backgroundColor: '#d1fae5',
          color: '#047857'
        };
  const attachmentDropZoneStyle: React.CSSProperties = {
    border: '1px dashed #d7cbb8',
    backgroundColor: '#fbf8f2',
    borderRadius: '0.75rem',
    padding: '1rem',
    cursor: 'pointer',
    display: 'grid',
    gap: '0.35rem'
  };
  const attachmentDropTitleStyle: React.CSSProperties = {
    color: '#003f46',
    fontWeight: 700,
    fontSize: '0.95rem'
  };
  const recipientPickerStyle: React.CSSProperties = {
    gridColumn: '1 / -1',
    border: '1px solid #d7cbb8',
    backgroundColor: '#fbf8f2',
    borderRadius: '0.75rem',
    padding: '0.85rem',
    display: 'grid',
    gap: '0.5rem'
  };
  const recipientSearchInputStyle: React.CSSProperties = {
    width: '100%',
    border: '1px solid #d7cbb8',
    borderRadius: '0.65rem',
    padding: '0.7rem 0.85rem',
    color: '#003f46',
    backgroundColor: '#fffdf8'
  };
  const recipientChipRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem'
  };
  const recipientActionRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'space-between'
  };
  const recipientChipStyle: React.CSSProperties = {
    border: '1px solid #b8d0cf',
    borderRadius: '999px',
    backgroundColor: '#e8f2f1',
    color: '#003f46',
    padding: '0.25rem 0.6rem',
    fontSize: '0.82rem',
    fontWeight: 700,
    cursor: 'pointer'
  };
  const recipientOptionListStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.4rem',
    maxHeight: '10rem',
    overflowY: 'auto',
    paddingRight: '0.25rem'
  };
  const recipientOptionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    padding: '0.65rem 0.75rem',
    border: '1px solid #e5d9c8',
    borderRadius: '0.65rem',
    backgroundColor: '#fffdf8',
    cursor: 'pointer'
  };
  const recipientMetaStyle: React.CSSProperties = {
    color: '#667085',
    fontSize: '0.82rem'
  };
  const externalContactListStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.5rem',
    maxHeight: '13rem',
    overflowY: 'auto',
    paddingRight: '0.25rem'
  };
  const externalComposeFlowStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.75rem',
    marginBottom: '0.85rem'
  };
  const externalStepPanelStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.65rem'
  };
  const externalStepHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
    alignItems: 'flex-start'
  };
  const externalStepTitleStyle: React.CSSProperties = {
    color: '#003f46',
    fontWeight: 800,
    fontSize: '0.95rem'
  };
  const externalCompactRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(13rem, 1fr))',
    gap: '0.65rem',
    alignItems: 'end'
  };
  const handleAttachmentDropZoneKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    inputRef.current?.click();
  };
  const handleComposeAttachmentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleTechMessageAttachmentChange(event.dataTransfer.files);
  };
  const handleReplyAttachmentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleTechReplyAttachmentChange(event.dataTransfer.files);
  };
  const toggleComposeRecipient = (recipientId: string) => {
    const setSelectedRecipientIds =
      messageChannelType === 'internal'
        ? setMessageComposeRecipientUserIds
        : setMessageComposeExternalRecipientUserIds;
    setSelectedRecipientIds(prev =>
      prev.includes(recipientId) ? prev.filter(id => id !== recipientId) : [...prev, recipientId]
    );
  };
  const activeRecipientOptions =
    messageChannelType === 'internal' ? composeInternalRecipientOptions : composeExternalRecipientOptions;
  const activeSelectedRecipientIds =
    messageChannelType === 'internal' ? messageComposeRecipientUserIds : messageComposeExternalRecipientUserIds;
  const isConnectedRecipientContext = messageComposeFocus === 'case' || messageComposeFocus === 'device';
  const autoRecipientOptions =
    messageChannelType === 'external'
      ? activeRecipientOptions.filter(person => !isConnectedRecipientContext || person.isContextMatched === true)
      : activeRecipientOptions;
  const selectedRecipientOptions = activeRecipientOptions.filter(person =>
    activeSelectedRecipientIds.includes(person.id)
  );
  const normalizedRecipientSearchTerm = recipientSearchTerm.trim().toLowerCase();
  const matchingRecipientOptions = activeRecipientOptions.filter(person => {
      if (!normalizedRecipientSearchTerm) return true;
      const searchable = [
        person.fullName,
        person.username,
        person.roleLabel,
        person.roleKey
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchable.includes(normalizedRecipientSearchTerm);
    });
  const externalRecipientContextKey = [
    messageComposeFocus,
    messageComposeCustomerId,
    messageComposeCaseId,
    messageComposeDeviceId,
    autoRecipientOptions.map(person => person.id).join(',')
  ].join(':');
  const devicesById = new Map<string, any>();
  devices.forEach(device => {
    if (device?.id) devicesById.set(device.id, device);
  });
  const getCustomerOptionLabel = (customer: any) =>
    customer?.companyName || customer?.company || customer?.name || 'Customer';
  const getDeviceCustomerId = (device: any) => String(device?.customerId || device?.customer?.id || '').trim();
  const getDeviceModelId = (device: any) => String(device?.modelId || device?.model?.id || '').trim();
  const getDeviceLocationId = (device: any) =>
    String(device?.locationId || device?.location?.id || device?.customerLocationId || '').trim();
  const getDeviceLocationLabel = (device: any) =>
    String(device?.location?.name || device?.locationName || device?.customerLocationName || '').trim();
  const getDeviceSearchText = (device: any) =>
    [
      getDeviceDisplayLabel(device),
      device?.serialNumber,
      device?.assetNumber,
      device?.customer?.companyName,
      device?.customer?.company,
      getDeviceLocationLabel(device)
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  const getCaseCustomerId = (entry: any) => String(entry?.customerId || entry?.customer?.id || '').trim();
  const getCaseDeviceId = (entry: any) => String(entry?.deviceId || entry?.device?.id || '').trim();
  const getCaseDevice = (entry: any) => entry?.device || devicesById.get(getCaseDeviceId(entry)) || null;
  const getCaseSearchText = (entry: any) =>
    [
      getCaseDisplayNumber(entry),
      entry?.title,
      entry?.subject,
      entry?.summary,
      entry?.description,
      entry?.customer?.companyName,
      entry?.customer?.company,
      getDeviceDisplayLabel(getCaseDevice(entry))
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  const normalizedCustomerSearchTerm = composeCustomerSearchTerm.trim().toLowerCase();
  const filteredComposeCustomerOptions = composeCustomerOptions.filter(customer => {
    if (!normalizedCustomerSearchTerm) return true;
    return [
      getCustomerOptionLabel(customer),
      customer?.email,
      customer?.phone,
      customer?.city,
      customer?.stateProvince,
      customer?.country
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(normalizedCustomerSearchTerm);
  });
  const caseCustomerOptions = composeCustomerOptions
    .filter(customer => cases.some(entry => getCaseCustomerId(entry) === customer.id))
    .slice()
    .sort((a, b) => getCustomerOptionLabel(a).localeCompare(getCustomerOptionLabel(b)));
  const caseDeviceOptions: Array<[string, string]> = Array.from(
    cases.reduce((map, entry) => {
      if (composeCaseCustomerFilter && getCaseCustomerId(entry) !== composeCaseCustomerFilter) return map;
      const deviceId = getCaseDeviceId(entry);
      const device = getCaseDevice(entry);
      if (deviceId && device) map.set(deviceId, getDeviceDisplayLabel(device));
      return map;
    }, new Map<string, string>())
  ) as Array<[string, string]>;
  caseDeviceOptions.sort((a, b) => a[1].localeCompare(b[1]));
  const normalizedCaseSearchTerm = composeCaseSearchTerm.trim().toLowerCase();
  const filteredComposeCaseOptions = cases.filter(entry => {
    if (composeCaseCustomerFilter && getCaseCustomerId(entry) !== composeCaseCustomerFilter) return false;
    if (composeCaseDeviceFilter && getCaseDeviceId(entry) !== composeCaseDeviceFilter) return false;
    if (normalizedCaseSearchTerm && !getCaseSearchText(entry).includes(normalizedCaseSearchTerm)) return false;
    return true;
  });
  const deviceCustomerOptions = composeCustomerOptions
    .filter(customer => composeDeviceOptions.some(device => getDeviceCustomerId(device) === customer.id))
    .slice()
    .sort((a, b) => getCustomerOptionLabel(a).localeCompare(getCustomerOptionLabel(b)));
  const deviceModelOptions: Array<[string, string]> = Array.from(
    composeDeviceOptions.reduce((map, device) => {
      if (composeDeviceCustomerFilter && getDeviceCustomerId(device) !== composeDeviceCustomerFilter) return map;
      const modelId = getDeviceModelId(device);
      const modelLabel = getDeviceDisplayLabel(device)
        .split(' | ')
        .find((part: string) => part.includes(' - ')) || getDeviceDisplayLabel(device);
      if (modelId && modelLabel) map.set(modelId, modelLabel);
      return map;
    }, new Map<string, string>())
  ) as Array<[string, string]>;
  deviceModelOptions.sort((a, b) => a[1].localeCompare(b[1]));
  const deviceLocationOptions: Array<[string, string]> = Array.from(
    composeDeviceOptions.reduce((map, device) => {
      if (composeDeviceCustomerFilter && getDeviceCustomerId(device) !== composeDeviceCustomerFilter) return map;
      if (composeDeviceModelFilter && getDeviceModelId(device) !== composeDeviceModelFilter) return map;
      const locationId = getDeviceLocationId(device);
      const locationLabel = getDeviceLocationLabel(device);
      if (locationId && locationLabel) map.set(locationId, locationLabel);
      return map;
    }, new Map<string, string>())
  ) as Array<[string, string]>;
  deviceLocationOptions.sort((a, b) => a[1].localeCompare(b[1]));
  const normalizedDeviceSearchTerm = composeDeviceSearchTerm.trim().toLowerCase();
  const filteredComposeDeviceOptions = composeDeviceOptions.filter(device => {
    if (composeDeviceCustomerFilter && getDeviceCustomerId(device) !== composeDeviceCustomerFilter) return false;
    if (composeDeviceModelFilter && getDeviceModelId(device) !== composeDeviceModelFilter) return false;
    if (composeDeviceLocationFilter && getDeviceLocationId(device) !== composeDeviceLocationFilter) return false;
    if (normalizedDeviceSearchTerm && !getDeviceSearchText(device).includes(normalizedDeviceSearchTerm)) return false;
    return true;
  });
  React.useEffect(() => {
    if (messageChannelType !== 'external') return;
    if (!messageComposeCustomerId) {
      setExternalRecipientSeedKey('');
      return;
    }
    if (messageComposeCustomerContactsLoading) return;
    if (externalRecipientSeedKey === externalRecipientContextKey) return;
    setMessageComposeExternalRecipientUserIds(autoRecipientOptions.map(person => person.id).filter(Boolean));
    setExternalRecipientSeedKey(externalRecipientContextKey);
  }, [
    autoRecipientOptions,
    externalRecipientContextKey,
    externalRecipientSeedKey,
    messageChannelType,
    messageComposeCustomerContactsLoading,
    messageComposeCustomerId,
    setMessageComposeExternalRecipientUserIds
  ]);
  const handleSelectVisibleRecipients = () => {
    const visibleIds = matchingRecipientOptions.map(person => person.id).filter(Boolean);
    const setSelectedRecipientIds =
      messageChannelType === 'internal'
        ? setMessageComposeRecipientUserIds
        : setMessageComposeExternalRecipientUserIds;
    setSelectedRecipientIds(prev => Array.from(new Set([...prev, ...visibleIds])));
  };
  const handleClearSelectedRecipients = () => {
    if (messageChannelType === 'internal') {
      setMessageComposeRecipientUserIds([]);
    } else {
      setMessageComposeExternalRecipientUserIds([]);
    }
  };
  const resetExternalAudience = () => {
    setMessageComposeExternalRecipientUserIds([]);
    setExternalRecipientSeedKey('');
    setRecipientSearchTerm('');
    setRecipientPickerOpen(false);
  };
  const selectExternalContextMode = (mode: 'general' | 'device' | 'case') => {
    setMessageComposeFocus(mode);
    setMessageComposeCaseId('');
    setMessageComposeDeviceId('');
    setMessageComposeCustomerId('');
    setComposeCustomerSearchTerm('');
    setComposeCaseSearchTerm('');
    setComposeCaseCustomerFilter('');
    setComposeCaseDeviceFilter('');
    setComposeDeviceSearchTerm('');
    setComposeDeviceCustomerFilter('');
    setComposeDeviceModelFilter('');
    setComposeDeviceLocationFilter('');
    resetExternalAudience();
  };
  const channelTabs = (
    <div style={styles.messagesChannelTabs}>
      <button
        type="button"
        style={{
          ...styles.messagesChannelTab,
          ...styles.messagesChannelTabExternal,
          ...(messageWorkspaceMode === 'messages' && messageChannelType === 'external'
            ? styles.messagesChannelTabActiveExternal
            : {})
        }}
        onClick={() => {
          setMessageWorkspaceMode('messages');
          setMessageChannelType('external');
          setSelectedNotificationId(null);
          setSelectedTechMessageId(null);
          setShowTechComposePanel(false);
        }}
      >
        <span>External</span>
        <span style={styles.messagesChannelTabCountExternal}>{messageUnreadCounts.external}</span>
      </button>
      <button
        type="button"
        style={{
          ...styles.messagesChannelTab,
          ...styles.messagesChannelTabInternal,
          ...(messageWorkspaceMode === 'messages' && messageChannelType === 'internal'
            ? styles.messagesChannelTabActiveInternal
            : {})
        }}
        onClick={() => {
          setMessageWorkspaceMode('messages');
          setMessageChannelType('internal');
          setSelectedNotificationId(null);
          setSelectedTechMessageId(null);
          setShowTechComposePanel(false);
        }}
      >
        <span>Internal</span>
        <span style={styles.messagesChannelTabCountInternal}>{messageUnreadCounts.internal}</span>
      </button>
      <button
        type="button"
        style={{
          ...styles.messagesChannelTab,
          ...notificationTabBaseStyle,
          ...(messageWorkspaceMode === 'notifications' ? notificationTabActiveStyle : {})
        }}
        onClick={() => {
          setMessageWorkspaceMode('notifications');
          setShowTechComposePanel(false);
          setSelectedTechMessageId(null);
        }}
      >
        <span>Notifications</span>
        <span style={{ ...styles.messagesChannelTabCountInternal, ...notificationCountStyle }}>
          {unreadNotificationCount}
        </span>
      </button>
    </div>
  );

  if (messageWorkspaceMode === 'notifications') {
    return (
      <>
        <div style={styles.header}>
          <div style={styles.headerTitleGroup}>
            <h1 style={styles.pageTitle}>Messages</h1>
            <div style={styles.pageSubtitle}>Review customer communications and broadcast notifications.</div>
          </div>
        </div>
        <div style={styles.messagesLayout}>
          <div style={styles.messagesSection}>
            <div style={styles.messagesSectionHeader}>
              <div>
                <div style={styles.messagesPanelTitle}>Notifications</div>
                <div style={styles.messagesPanelSubtitle}>App-opening notices created by the service team.</div>
              </div>
              {canCreateNotifications && (
                <button type="button" style={styles.messagesNewButton} onClick={onCreateNotification}>
                  New notification
                </button>
              )}
            </div>
            {channelTabs}
            <div style={styles.messagesFilterRow}>
              <select
                style={styles.filterSelect}
                value={notificationView}
                onChange={(event) => {
                  setNotificationView(event.target.value as 'active' | 'all' | 'dismissed');
                  setSelectedNotificationId(null);
                }}
              >
                <option value="active">Active</option>
                <option value="all">All</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
            {notificationsLoading ? (
              <AppLoadingState
                title="Loading notifications"
                message="Fetching notifications for this account."
                layout="rows"
                density="section"
              />
            ) : notifications.length === 0 ? (
              <div style={styles.messagesEmpty}>
                {notificationView === 'active' ? 'No active notifications.' : 'No notifications match this view.'}
              </div>
            ) : (
              <div style={styles.messagesListScroll}>
                <div style={styles.messagesList}>
                  {notifications.map(notification => {
                    const isUnread = !notification.readAt && !notification.dismissedAt;
                    const isDismissed = Boolean(notification.dismissedAt);
                    const attachmentsCount = Array.isArray(notification.attachments) ? notification.attachments.length : 0;
                    return (
                      <button
                        key={notification.id}
                        type="button"
                        style={{
                          ...styles.messagesItem,
                          ...(selectedNotificationId === notification.id ? styles.messagesItemActiveInternal : {})
                        }}
                        onClick={() => {
                          setSelectedNotificationId(notification.id);
                          if (isUnread) {
                            markNotificationRead(notification.id);
                          }
                        }}
                      >
                        <div style={styles.messagesItemHeader}>
                          <div style={styles.messagesItemHeaderLeft}>
                            <div style={styles.messagesItemBadgeRow}>
                              <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeInternal }}>
                                {notification.severity || 'info'}
                              </span>
                              {isUnread && (
                                <span style={{ ...styles.messagesUnreadBadge, ...styles.messagesUnreadBadgeInternal }}>
                                  New
                                </span>
                              )}
                              {isDismissed && <span style={styles.messagesStateBadge}>Dismissed</span>}
                            </div>
                            <div style={styles.messagesItemTitle}>{notification.title}</div>
                          </div>
                        </div>
                        <div style={styles.messagesItemBody}>{getMessagePreview(notification.body)}</div>
                        <div style={styles.messagesItemMetaRow}>
                          <div style={styles.messagesItemMeta}>
                            {formatDateTime(notification.publishedAt || notification.createdAt) || 'Recently'}
                          </div>
                          {attachmentsCount > 0 && (
                            <div style={styles.messagesItemMeta}>
                              {attachmentsCount} attachment{attachmentsCount === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {selectedNotification && (
            <div style={styles.messagesSection}>
              <div style={styles.messagesDetailCard}>
                <div style={styles.messagesDetailHeader}>
                  <div>
                    <div style={styles.messagesDetailTitle}>{selectedNotification.title}</div>
                    <div style={styles.messagesItemMeta}>
                      {formatDateTime(selectedNotification.publishedAt || selectedNotification.createdAt) || 'Recently'}
                    </div>
                  </div>
                  <div style={styles.messagesItemBadgeRow}>
                    <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeInternal }}>
                      {selectedNotification.severity || 'info'}
                    </span>
                    {selectedNotification.dismissedAt && <span style={styles.messagesStateBadge}>Dismissed</span>}
                  </div>
                </div>
                <div style={styles.messagesDetailBody}>{selectedNotification.body}</div>
                {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
                  <div style={styles.messagesDetailAttachments}>
                    {selectedNotification.attachments.map(item => (
                      <div key={item.id} style={styles.messagesAttachmentItem}>
                        <button
                          type="button"
                          style={styles.messagesAttachmentRemove}
                          onClick={() => void handleOpenTechNotificationAttachment(selectedNotification, item)}
                        >
                          {item.name} ({formatAttachmentSize(Number(item.size || 0))})
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {!selectedNotification.dismissedAt && (
                  <div style={styles.messagesReplyActions}>
                    <button
                      type="button"
                      style={styles.messagesReplySecondaryButton}
                      onClick={() => dismissNotification(selectedNotification)}
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div style={styles.header}>
        <div style={styles.headerTitleGroup}>
          <h1 style={styles.pageTitle}>Messages</h1>
          <div style={styles.pageSubtitle}>Review and respond to customer communications.</div>
        </div>
      </div>
      <div style={styles.messagesLayout}>
        <div style={styles.messagesSection}>
          <div style={styles.messagesSectionHeader}>
            <div>
              <div style={styles.messagesPanelTitle}>Inbox</div>
              <div style={styles.messagesPanelSubtitle}>
                {showInboxQueryLoading
                  ? 'Loading inbox messages...'
                  : messagesLoading
                  ? 'Refreshing messages...'
                  : 'Filter by focus to route updates quickly.'}
              </div>
            </div>
            <button
              type="button"
              style={styles.messagesNewButton}
              onClick={() => {
                setShowTechComposePanel(!showTechComposePanel);
                setSelectedTechMessageId(null);
              }}
            >
              {showTechComposePanel ? 'Hide new message' : 'New message'}
            </button>
          </div>
          {channelTabs}
          <div style={styles.messagesFilterRow}>
            <select
              style={styles.filterSelect}
              value={messageView}
              onChange={(event) => setMessageView(event.target.value as 'active' | 'archived' | 'all')}
            >
              <option value='active'>Active</option>
              <option value='archived'>Archived</option>
              <option value='all'>All</option>
            </select>
            <select
              style={styles.filterSelect}
              value={messageFocusFilter}
              onChange={(event) => {
                const value = event.target.value as 'all' | 'general' | 'device' | 'case';
                setMessageFocusFilter(value);
                setMessageCaseFilter('');
                setMessageDeviceFilter('');
              }}
            >
              <option value='all'>All Focus</option>
              <option value='general'>General</option>
              <option value='case'>Case</option>
              <option value='device'>Device</option>
            </select>
            <select
              style={styles.filterSelect}
              value={messageCaseFilter}
              onChange={(event) => setMessageCaseFilter(event.target.value)}
              disabled={messageFocusFilter === 'device'}
            >
              <option value=''>All Cases</option>
              {messageCaseOptions.map(entry => (
                <option key={entry.id} value={entry.id}>
                  {getCaseDisplayNumber(entry)}
                </option>
              ))}
            </select>
            <select
              style={styles.filterSelect}
              value={messageDeviceFilter}
              onChange={(event) => setMessageDeviceFilter(event.target.value)}
              disabled={messageFocusFilter === 'case'}
            >
              <option value=''>All Devices</option>
              {messageDeviceOptions.map(device => (
                <option key={device.id} value={device.id}>
                  {getDeviceDisplayLabel(device)}
                </option>
              ))}
            </select>
            <select
              style={styles.filterSelect}
              value={messageSortOrder}
              onChange={(event) => setMessageSortOrder(event.target.value as 'newest' | 'oldest')}
            >
              <option value='newest'>Newest first</option>
              <option value='oldest'>Oldest first</option>
            </select>
            <label style={styles.messagesFilterToggle}>
              <input
                type="checkbox"
                style={styles.messagesFilterToggleInput}
                checked={showUnreadOnly}
                onChange={(event) => setShowUnreadOnly(event.target.checked)}
              />
              New only
            </label>
            {messageChannelType === 'external' && (
              <label style={styles.messagesFilterToggle}>
                <input
                  type="checkbox"
                  style={styles.messagesFilterToggleInput}
                  checked={messageShowSent}
                  onChange={(event) => setMessageShowSent(event.target.checked)}
                />
                Show sent
              </label>
            )}
          </div>
          {showInboxQueryLoading ? (
            <AppLoadingState
              title="Loading inbox"
              message="Fetching messages for the selected channel and view."
              hint="The message list will appear once this inbox query finishes."
              layout="rows"
              density="section"
            />
          ) : filteredTechMessages.length === 0 ? (
            <div style={styles.messagesEmpty}>
              {techMessages.length === 0 ? 'No messages yet.' : 'No messages match the current filters.'}
              {techMessages.length > 0 && (
                <button
                  type='button'
                  style={styles.messagesClearFilters}
                  onClick={() => {
                    setMessageFocusFilter('all');
                    setMessageCaseFilter('');
                    setMessageDeviceFilter('');
                    setShowUnreadOnly(false);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div style={styles.messagesListScroll}>
              <div style={styles.messagesList}>
                {filteredTechMessages.slice(0, messageVisibleCount).map(message => {
                  const isUnread =
                    messageChannelType === 'internal'
                      ? message.senderUserId !== user?.id && !readTechMessageIds.includes(message.id)
                      : message.senderRole === 'customer' && !readTechMessageIds.includes(message.id);
                  const attachmentsCount = message.attachments ? message.attachments.length : 0;
                  const isInternalMessage = messageChannelType === 'internal';
                  const isCurrentUserSender = message.senderUserId === user?.id;
                  const isSupportMessage = !isInternalMessage && message.senderRole !== 'customer';
                  const senderLabel = isInternalMessage
                    ? isCurrentUserSender
                      ? message.recipientDisplayName || 'Staff'
                      : message.senderDisplayName || 'Staff'
                    : message.customerName || message.senderDisplayName || 'Customer';
                  const senderBadgeLabel = isInternalMessage
                    ? isCurrentUserSender
                      ? 'You'
                      : 'Internal'
                    : isSupportMessage
                    ? 'Support'
                    : 'Customer';
                  const isArchived = Boolean(message.archivedByStaffAt || message.archivedByCustomerAt);
                  return (
                    <button
                      key={message.id}
                      type='button'
                      style={{
                        ...styles.messagesItem,
                        ...(selectedTechMessageId === message.id
                          ? messageChannelType === 'internal'
                            ? styles.messagesItemActiveInternal
                            : styles.messagesItemActiveExternal
                          : {})
                      }}
                      onClick={() => {
                        setSelectedTechMessageId(message.id);
                        setShowTechComposePanel(false);
                        if (isUnread) {
                          setReadTechMessageIds([...readTechMessageIds, message.id]);
                          markTechMessageRead(message.id);
                        }
                      }}
                    >
                      <div style={styles.messagesItemHeader}>
                        <div style={styles.messagesItemHeaderLeft}>
                          <div style={styles.messagesItemBadgeRow}>
                            <span
                              style={{
                                ...styles.messagesSenderBadge,
                                ...(isSupportMessage ? styles.messagesSenderBadgeSupport : {}),
                                ...(isInternalMessage ? styles.messagesSenderBadgeInternal : {})
                              }}
                            >
                              {senderBadgeLabel}
                            </span>
                            {isUnread && (
                              <span
                                style={{
                                  ...styles.messagesUnreadBadge,
                                  ...(isInternalMessage
                                    ? styles.messagesUnreadBadgeInternal
                                    : styles.messagesUnreadBadgeExternal)
                                }}
                              >
                                New
                              </span>
                            )}
                            {isArchived && <span style={styles.messagesStateBadge}>Archived</span>}
                          </div>
                          <div style={styles.messagesItemTitle}>{senderLabel}</div>
                        </div>
                        <div style={styles.messagesItemBadgeRow}>
                          <span style={styles.messagesFocusBadge}>{getTechMessageFocusLabel(message)}</span>
                        </div>
                      </div>
                      <div style={styles.messagesItemBody}>{getMessagePreview(message.body)}</div>
                      <div style={styles.messagesItemMetaRow}>
                        <div style={styles.messagesItemMeta}>
                          {formatDateTime(message.createdAt) || 'Just now'}
                        </div>
                        <div style={styles.messagesItemMetaGroup}>
                          {attachmentsCount > 0 && (
                            <span
                              style={styles.messagesItemMetaTag}
                            >
                              {attachmentsCount} attachment{attachmentsCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
                {filteredTechMessages.length > messageVisibleCount && (
                  <button
                    type="button"
                    style={styles.messagesLoadMore}
                    onClick={() => setMessageVisibleCount(messageVisibleCount + 10)}
                  >
                    Load older messages
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showTechComposePanel && (
        <div style={styles.modal} onClick={() => setShowTechComposePanel(false)}>
          <div
            style={{ ...styles.modalContentWide, ...styles.messagesModalCard, maxWidth: '900px' }}
            onClick={(e) => e.stopPropagation()}
          >
              <div style={styles.messagesModalHeader}>
                <div style={styles.messagesComposeTitle}>New message</div>
                <button
                type='button'
                style={styles.messagesModalCloseButton}
                onClick={() => setShowTechComposePanel(false)}
              >
                  Close
                </button>
              </div>
              {messageChannelType === 'internal' ? (
              <div style={styles.messagesComposeGrid}>
                <div style={recipientPickerStyle}>
                  <div style={styles.messagesLabel}>Internal recipients</div>
                  <input
                    type='search'
                    style={recipientSearchInputStyle}
                    value={recipientSearchTerm}
                    onFocus={() => setRecipientPickerOpen(true)}
                    onChange={(event) => {
                      setRecipientSearchTerm(event.target.value);
                      setRecipientPickerOpen(true);
                    }}
                    placeholder='Search team members by name, username, or role...'
                  />
                  <div style={recipientActionRowStyle}>
                    <div style={recipientMetaStyle}>
                      {activeSelectedRecipientIds.length} selected
                      {normalizedRecipientSearchTerm ? ` | ${matchingRecipientOptions.length} shown` : ''}
                    </div>
                    <div style={recipientChipRowStyle}>
                      <button
                        type='button'
                        style={styles.messagesModalCloseButton}
                        onClick={handleSelectVisibleRecipients}
                        disabled={matchingRecipientOptions.length === 0}
                      >
                        {normalizedRecipientSearchTerm ? 'Select shown' : 'Select all'}
                      </button>
                      <button
                        type='button'
                        style={styles.messagesModalCloseButton}
                        onClick={handleClearSelectedRecipients}
                        disabled={activeSelectedRecipientIds.length === 0}
                      >
                        Clear all
                      </button>
                    </div>
                  </div>
                  {selectedRecipientOptions.length > 0 && (
                    <div style={recipientChipRowStyle}>
                      {selectedRecipientOptions.map(person => (
                        <button
                          key={person.id}
                          type='button'
                          style={recipientChipStyle}
                          onClick={() => toggleComposeRecipient(person.id)}
                          title='Remove recipient'
                        >
                          {person.fullName || person.username || 'Team member'} x
                        </button>
                      ))}
                    </div>
                  )}
                  {activeRecipientOptions.length === 0 ? (
                    <div style={recipientMetaStyle}>No active internal recipients available.</div>
                  ) : recipientPickerOpen ? (
                    <div style={recipientOptionListStyle}>
                      {matchingRecipientOptions.length === 0 ? (
                        <div style={recipientMetaStyle}>No matching team members.</div>
                      ) : (
                        matchingRecipientOptions.map(person => {
                          const displayName = person.fullName || person.username || 'Team member';
                          const isSelected = activeSelectedRecipientIds.includes(person.id);
                          return (
                            <label key={person.id} style={recipientOptionStyle}>
                              <input
                                type='checkbox'
                                checked={isSelected}
                                onChange={() => {
                                  toggleComposeRecipient(person.id);
                                  setRecipientSearchTerm('');
                                }}
                              />
                              <span>
                                <strong>{displayName}</strong>
                                {(person.roleLabel || person.username) && (
                                  <span style={recipientMetaStyle}>
                                    {' '}
                                    {person.roleLabel || person.username}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  ) : (
                    <button
                      type='button'
                      style={styles.messagesModalCloseButton}
                      onClick={() => setRecipientPickerOpen(true)}
                    >
                      Browse team members
                    </button>
                  )}
                </div>
                <select
                  style={styles.filterSelect}
                  value={messageComposeFocus}
                  onChange={(event) => {
                    const value = event.target.value as 'general' | 'device' | 'case';
                    setMessageComposeFocus(value);
                    setMessageComposeCaseId('');
                    setMessageComposeDeviceId('');
                  }}
                >
                  <option value='general'>General</option>
                  <option value='case'>Case</option>
                  <option value='device'>Device</option>
                </select>
                {messageComposeFocus === 'case' && (
                  <select
                    style={styles.filterSelect}
                    value={messageComposeCaseId}
                    onChange={(event) => setMessageComposeCaseId(event.target.value)}
                  >
                    <option value=''>Select case</option>
                    {composeCaseOptions.map(entry => (
                      <option key={entry.id} value={entry.id}>
                        {getCaseDisplayNumber(entry)}
                      </option>
                    ))}
                  </select>
                )}
                {messageComposeFocus === 'device' && (
                  <select
                    style={styles.filterSelect}
                    value={messageComposeDeviceId}
                    onChange={(event) => setMessageComposeDeviceId(event.target.value)}
                  >
                    <option value=''>Select device</option>
                    {composeDeviceOptions.map(device => (
                      <option key={device.id} value={device.id}>
                        {getDeviceDisplayLabel(device)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : (
              <div style={externalComposeFlowStyle}>
                <div style={externalStepPanelStyle}>
                  <>
                      <div style={externalCompactRowStyle}>
                        <div style={styles.messagesField}>
                          <label style={styles.messagesLabel}>Target</label>
                          <select
                            style={styles.filterSelect}
                            value={messageComposeFocus}
                            onChange={(event) => selectExternalContextMode(event.target.value as 'general' | 'case' | 'device')}
                          >
                            <option value='general'>Customer</option>
                            <option value='case'>Case</option>
                            <option value='device'>Unique device</option>
                          </select>
                        </div>
                        {messageComposeFocus === 'general' && (
                          <div style={styles.messagesField}>
                            <label style={styles.messagesLabel}>Search</label>
                            <input
                              type='search'
                              style={recipientSearchInputStyle}
                              value={composeCustomerSearchTerm}
                              onChange={(event) => setComposeCustomerSearchTerm(event.target.value)}
                              placeholder='Name, phone, email, city...'
                            />
                          </div>
                        )}
                      </div>
                      {messageComposeFocus === 'general' && (
                        <div style={externalCompactRowStyle}>
                          <div style={styles.messagesField}>
                            <label style={styles.messagesLabel}>Customer</label>
                            <select
                              style={styles.filterSelect}
                              value={messageComposeCustomerId}
                              onChange={(event) => {
                                setMessageComposeCustomerId(event.target.value);
                                setMessageComposeCaseId('');
                                setMessageComposeDeviceId('');
                                resetExternalAudience();
                              }}
                            >
                              <option value=''>Select customer</option>
                              {filteredComposeCustomerOptions.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                  {getCustomerOptionLabel(customer)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {messageComposeFocus === 'case' && (
                        <div style={{ display: 'grid', gap: '0.45rem' }}>
                          <div style={externalCompactRowStyle}>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Customer</label>
                              <select
                                style={styles.filterSelect}
                                value={composeCaseCustomerFilter}
                                onChange={(event) => {
                                  setComposeCaseCustomerFilter(event.target.value);
                                  setComposeCaseDeviceFilter('');
                                  setMessageComposeCaseId('');
                                  setMessageComposeCustomerId('');
                                  resetExternalAudience();
                                }}
                              >
                                <option value=''>All customers</option>
                                {caseCustomerOptions.map(customer => (
                                  <option key={customer.id} value={customer.id}>
                                    {getCustomerOptionLabel(customer)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Device</label>
                              <select
                                style={styles.filterSelect}
                                value={composeCaseDeviceFilter}
                                onChange={(event) => {
                                  setComposeCaseDeviceFilter(event.target.value);
                                  setMessageComposeCaseId('');
                                  setMessageComposeCustomerId('');
                                  resetExternalAudience();
                                }}
                              >
                                <option value=''>All unique devices</option>
                                {caseDeviceOptions.map(([deviceId, deviceLabel]) => (
                                  <option key={deviceId} value={deviceId}>
                                    {deviceLabel}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Search</label>
                              <input
                                type='search'
                                style={recipientSearchInputStyle}
                                value={composeCaseSearchTerm}
                                onChange={(event) => setComposeCaseSearchTerm(event.target.value)}
                                placeholder='Case, subject, device...'
                              />
                            </div>
                          </div>
                          <div style={styles.messagesField}>
                            <label style={styles.messagesLabel}>Case</label>
                            <select
                              style={styles.filterSelect}
                              value={messageComposeCaseId}
                              onChange={(event) => {
                                const nextCaseId = event.target.value;
                                setMessageComposeCaseId(nextCaseId);
                                setMessageComposeDeviceId('');
                                resetExternalAudience();
                                const matched = cases.find(entry => entry.id === nextCaseId);
                                setMessageComposeCustomerId(matched?.customerId || matched?.customer?.id || '');
                              }}
                            >
                              <option value=''>Select case</option>
                              {filteredComposeCaseOptions.map(entry => (
                                <option key={entry.id} value={entry.id}>
                                  {getCaseDisplayNumber(entry)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {messageComposeFocus === 'device' && (
                        <div style={{ display: 'grid', gap: '0.45rem' }}>
                          <div style={externalCompactRowStyle}>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Customer</label>
                              <select
                                style={styles.filterSelect}
                                value={composeDeviceCustomerFilter}
                                onChange={(event) => {
                                  setComposeDeviceCustomerFilter(event.target.value);
                                  setComposeDeviceModelFilter('');
                                  setComposeDeviceLocationFilter('');
                                  setMessageComposeDeviceId('');
                                  setMessageComposeCustomerId('');
                                  resetExternalAudience();
                                }}
                              >
                                <option value=''>All customers</option>
                                {deviceCustomerOptions.map(customer => (
                                  <option key={customer.id} value={customer.id}>
                                    {getCustomerOptionLabel(customer)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Model</label>
                              <select
                                style={styles.filterSelect}
                                value={composeDeviceModelFilter}
                                onChange={(event) => {
                                  setComposeDeviceModelFilter(event.target.value);
                                  setComposeDeviceLocationFilter('');
                                  setMessageComposeDeviceId('');
                                  setMessageComposeCustomerId('');
                                  resetExternalAudience();
                                }}
                              >
                                <option value=''>All models</option>
                                {deviceModelOptions.map(([modelId, modelLabel]) => (
                                  <option key={modelId} value={modelId}>
                                    {modelLabel}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Location</label>
                              <select
                                style={styles.filterSelect}
                                value={composeDeviceLocationFilter}
                                onChange={(event) => {
                                  setComposeDeviceLocationFilter(event.target.value);
                                  setMessageComposeDeviceId('');
                                  setMessageComposeCustomerId('');
                                  resetExternalAudience();
                                }}
                              >
                                <option value=''>All locations</option>
                                {deviceLocationOptions.map(([locationId, locationLabel]) => (
                                  <option key={locationId} value={locationId}>
                                    {locationLabel}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={styles.messagesField}>
                              <label style={styles.messagesLabel}>Search</label>
                              <input
                                type='search'
                                style={recipientSearchInputStyle}
                                value={composeDeviceSearchTerm}
                                onChange={(event) => setComposeDeviceSearchTerm(event.target.value)}
                                placeholder='Asset, serial, customer, location...'
                              />
                            </div>
                          </div>
                          <div style={styles.messagesField}>
                            <label style={styles.messagesLabel}>Unique device</label>
                            <select
                              style={styles.filterSelect}
                              value={messageComposeDeviceId}
                              onChange={(event) => {
                                const nextDeviceId = event.target.value;
                                setMessageComposeDeviceId(nextDeviceId);
                                resetExternalAudience();
                                const matched = devices.find(device => device.id === nextDeviceId);
                                setMessageComposeCustomerId(matched?.customerId || matched?.customer?.id || '');
                              }}
                            >
                              <option value=''>Select unique device</option>
                              {filteredComposeDeviceOptions.map(device => (
                                <option key={device.id} value={device.id}>
                                  {getDeviceDisplayLabel(device)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                  </>
                </div>

                <div style={externalStepPanelStyle}>
                  <div style={externalStepHeaderStyle}>
                    <div>
                      <div style={externalStepTitleStyle}>Recipients</div>
                    </div>
                  </div>
                  {messageComposeCustomerId && (
                    <>
                      <div style={recipientActionRowStyle}>
                        <div style={recipientMetaStyle}>
                          {activeSelectedRecipientIds.length} selected
                          {isConnectedRecipientContext ? ` | ${autoRecipientOptions.length} auto-matched` : ''}
                        </div>
                        <div style={recipientChipRowStyle}>
                          <button
                            type='button'
                            style={styles.messagesModalCloseButton}
                            onClick={() => setRecipientPickerOpen(prev => !prev)}
                          >
                            {recipientPickerOpen ? 'Hide recipients' : 'Edit recipients'}
                          </button>
                          {isConnectedRecipientContext && (
                            <button
                              type='button'
                              style={styles.messagesModalCloseButton}
                              onClick={() =>
                                setMessageComposeExternalRecipientUserIds(autoRecipientOptions.map(person => person.id).filter(Boolean))
                              }
                              disabled={autoRecipientOptions.length === 0}
                            >
                              Use auto-matched
                            </button>
                          )}
                          <button
                            type='button'
                            style={styles.messagesModalCloseButton}
                            onClick={() =>
                              setMessageComposeExternalRecipientUserIds(activeRecipientOptions.map(person => person.id).filter(Boolean))
                            }
                            disabled={activeRecipientOptions.length === 0}
                          >
                            Select all
                          </button>
                          <button
                            type='button'
                            style={styles.messagesModalCloseButton}
                            onClick={handleClearSelectedRecipients}
                            disabled={activeSelectedRecipientIds.length === 0}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      {selectedRecipientOptions.length > 0 && (
                        <div style={recipientChipRowStyle}>
                          {selectedRecipientOptions.slice(0, 5).map(person => (
                            <button
                              key={person.id}
                              type='button'
                              style={recipientChipStyle}
                              onClick={() => toggleComposeRecipient(person.id)}
                              title='Remove contact'
                            >
                              {person.fullName || person.username || 'Customer contact'} x
                            </button>
                          ))}
                          {selectedRecipientOptions.length > 5 && (
                            <span style={recipientMetaStyle}>+{selectedRecipientOptions.length - 5} more</span>
                          )}
                        </div>
                      )}
                      {recipientPickerOpen && (
                        <div style={{ display: 'grid', gap: '0.65rem' }}>
                          <input
                            type='search'
                            style={recipientSearchInputStyle}
                            value={recipientSearchTerm}
                            onChange={(event) => setRecipientSearchTerm(event.target.value)}
                            placeholder='Filter contacts by name, email, role, or location...'
                          />
                          {messageComposeCustomerContactsLoading ? (
                            <div style={recipientMetaStyle}>Loading customer contacts...</div>
                          ) : activeRecipientOptions.length === 0 ? (
                            <div style={recipientMetaStyle}>No active portal contacts are linked to this customer.</div>
                          ) : (
                            <div style={externalContactListStyle}>
                              {matchingRecipientOptions.length === 0 ? (
                                <div style={recipientMetaStyle}>No matching contacts.</div>
                              ) : (
                                matchingRecipientOptions.map(person => {
                                  const displayName = person.fullName || person.username || 'Customer contact';
                                  const isSelected = activeSelectedRecipientIds.includes(person.id);
                                  return (
                                    <label key={person.id} style={recipientOptionStyle}>
                                      <input
                                        type='checkbox'
                                        checked={isSelected}
                                        onChange={() => toggleComposeRecipient(person.id)}
                                      />
                                      <span>
                                        <strong>{displayName}</strong>
                                        {(person.roleLabel || person.email || person.username) && (
                                          <span style={recipientMetaStyle}>
                                            {' '}
                                            {[person.roleLabel, person.email || person.username].filter(Boolean).join(' | ')}
                                          </span>
                                        )}
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <textarea
              style={styles.messagesComposeInput}
              placeholder={
                messageChannelType === 'internal'
                  ? 'Write your message to your team...'
                  : 'Write your message to the customer...'
              }
              rows={5}
              value={messageComposeBody}
              onChange={(event) => setMessageComposeBody(event.target.value)}
            />
            <div style={{ ...styles.messagesField, marginTop: '0.25rem' }}>
              <label style={styles.messagesLabel}>Attachments</label>
              <div
                style={attachmentDropZoneStyle}
                role='button'
                tabIndex={0}
                onClick={() => composeAttachmentInputRef.current?.click()}
                onKeyDown={(event) => handleAttachmentDropZoneKeyDown(event, composeAttachmentInputRef)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleComposeAttachmentDrop}
              >
                <div style={attachmentDropTitleStyle}>Drop files or click to attach</div>
              </div>
              <input
                ref={composeAttachmentInputRef}
                type='file'
                multiple
                accept='image/*,video/*,audio/*'
                onChange={(event) => {
                  handleTechMessageAttachmentChange(event.currentTarget.files);
                  event.currentTarget.value = '';
                }}
                style={{ display: 'none' }}
              />
              {messageComposeAttachments.length > 0 && (
                <div style={styles.messagesAttachmentList}>
                  {messageComposeAttachments.map(item => (
                    <div key={item.id} style={styles.messagesAttachmentItem}>
                      <div>
                        {item.name} ({formatAttachmentSize(item.size)})
                      </div>
                      <button
                        type='button'
                        style={styles.messagesAttachmentRemove}
                        onClick={() => handleRemoveTechMessageAttachment(item.id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={styles.messagesComposeActions}>
              <button
                type='button'
                style={styles.messagesReplyButton}
                onClick={handleSendOutboundMessage}
                disabled={
                  !messageComposeBody.trim() ||
                  messageActionBusy !== null ||
                  (messageChannelType === 'internal' && messageComposeRecipientUserIds.length === 0) ||
                  (messageChannelType === 'external' &&
                    (!messageComposeCustomerId || messageComposeExternalRecipientUserIds.length === 0))
                }
              >
                {messageActionBusy === 'compose' ? 'Sending...' : 'Send message'}
              </button>
            </div>
          </div>
        </div>
      )}
      {selectedTechMessage && !showTechComposePanel && (
        <div style={styles.modal} onClick={() => setSelectedTechMessageId(null)}>
          <div
            style={{ ...styles.modalContentWide, ...styles.messagesModalCard, maxWidth: '900px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.messagesModalHeader}>
              <div style={styles.messagesDetailTitle}>
                {selectedTechMessage.channelType === 'internal'
                  ? selectedTechMessage.senderUserId === user?.id
                    ? selectedTechMessage.recipientDisplayName || 'Internal message'
                    : selectedTechMessage.senderDisplayName || 'Internal message'
                  : selectedTechMessage.customerName || 'Customer message'}
              </div>
              <button
                type='button'
                style={styles.messagesModalCloseButton}
                onClick={() => setSelectedTechMessageId(null)}
              >
                Close
              </button>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={styles.messagesItemMeta}>Focus: {getTechMessageFocusLabel(selectedTechMessage)}</div>
              <div style={styles.messagesItemMeta}>
                {formatDateTime(selectedTechMessage.createdAt) || 'Just now'}
              </div>
              <div style={styles.messagesDetailBody}>{selectedTechMessage.body}</div>
            </div>
            {selectedTechMessage.attachments && selectedTechMessage.attachments.length > 0 && (
              <div style={styles.messagesDetailAttachments}>
                {selectedTechMessage.attachments.map((item: any) => (
                  <div key={item.id} style={styles.messagesAttachmentItem}>
                    <button
                      type='button'
                      style={styles.messagesAttachmentRemove}
                      onClick={() => void handleDownloadTechMessageAttachment(selectedTechMessage.id, item)}
                    >
                      {item.name} ({formatAttachmentSize(item.size)})
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={styles.messagesReplyPanel}>
              <div style={styles.messagesReplyTitle}>Reply</div>
              <textarea
                style={styles.messagesReplyInput}
                placeholder={
                  selectedTechMessage.channelType === 'internal'
                    ? 'Write a reply to your team...'
                    : 'Write a reply to the customer...'
                }
                rows={4}
                value={messageReplyBody}
                onChange={(event) => setMessageReplyBody(event.target.value)}
              />
              <div style={styles.messagesField}>
                <label style={styles.messagesLabel}>Attachments</label>
                <div
                  style={attachmentDropZoneStyle}
                  role='button'
                  tabIndex={0}
                  onClick={() => replyAttachmentInputRef.current?.click()}
                  onKeyDown={(event) => handleAttachmentDropZoneKeyDown(event, replyAttachmentInputRef)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleReplyAttachmentDrop}
                >
                  <div style={attachmentDropTitleStyle}>Drop files or click to attach</div>
                </div>
                <input
                  ref={replyAttachmentInputRef}
                  type='file'
                  multiple
                  accept='image/*,video/*,audio/*'
                  onChange={(event) => {
                    handleTechReplyAttachmentChange(event.currentTarget.files);
                    event.currentTarget.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                {messageReplyAttachments.length > 0 && (
                  <div style={styles.messagesAttachmentList}>
                    {messageReplyAttachments.map(item => (
                      <div key={item.id} style={styles.messagesAttachmentItem}>
                        <div>
                          {item.name} ({formatAttachmentSize(item.size)})
                        </div>
                        <button
                          type='button'
                          style={styles.messagesAttachmentRemove}
                          onClick={() => handleRemoveTechReplyAttachment(item.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.messagesReplyActions}>
                {messageView !== 'archived' && (
                  <button
                    type='button'
                    style={styles.messagesReplyButtonSecondary}
                    onClick={() => void handleArchiveSelectedTechMessage(true)}
                  >
                    Archive
                  </button>
                )}
                {messageView === 'archived' && (
                  <button
                    type='button'
                    style={styles.messagesReplyButtonSecondary}
                    onClick={() => void handleRestoreSelectedTechMessage()}
                  >
                    Restore
                  </button>
                )}
                <button
                  type='button'
                  style={styles.messagesReplyButtonGhost}
                  onClick={() => void handleDeleteSelectedTechMessage()}
                >
                  {messageView === 'archived' ? 'Restore thread' : 'Archive thread'}
                </button>
                <button
                  type='button'
                  style={styles.messagesReplyButton}
                  onClick={handleSendTechReply}
                  disabled={!messageReplyBody.trim() || messageActionBusy !== null}
                >
                  {messageActionBusy === 'reply' ? 'Sending...' : 'Send reply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
